// The device's page document (GET /v1/devices/{id}/profile): the merged profile entries with their device-profile
// 0.4.0 fields. The device page reads three things from it (specs/device-dashboards): each datapoint's normal band,
// the default page (`ui.dashboard`) and the suggested widgets (`ui.widgets`).
import { queryOptions } from "@tanstack/react-query";
import { apiClient, orgHeader, unwrap } from "../../../api/client";
import type { components } from "../../../api/generated/vision-api";
import type { CreavisionDashboardDocument } from "../../../documents/generated/dashboard";
import { toBand, type Band } from "../../../widgets/expected";
import type { Widget } from "../../../widgets/types";

export type PageDocument = components["schemas"]["PageDocument"];

/** The page document in the app's language; the server caches it by ETag. */
export const profileQuery = (orgId: string, id: string, language: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/profile", id, language],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/profile", {
          params: { path: { device_id: id }, header: { "Accept-Language": language, ...orgHeader(orgId) } },
        }),
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);

/** Each numeric datapoint's `expected` band, by key (DDB-01). */
export function expectedBands(doc: PageDocument | undefined): Record<string, Band> {
  const out: Record<string, Band> = {};
  for (const dp of doc?.datapoints ?? []) {
    const band = toBand(dp.expected);
    if (typeof dp.key === "string" && band) out[dp.key] = band;
  }
  return out;
}

/**
 * The page document's `ui` as the profile gives it. vision-api 0.16.0 lists `card`, `page` and `map_icon` only;
 * `widgets` and `dashboard` (device-profile 0.4.0) pass through until the contract names them (contract request
 * app-web-device-dashboards).
 */
const profileUi = (doc: PageDocument | undefined): Record<string, unknown> => (isObject(doc?.ui) ? doc.ui : {});

/** `ui.dashboard` when it is a device page with widgets (DDB-20); the page falls back to its sections otherwise. */
export function defaultPage(doc: PageDocument | undefined): CreavisionDashboardDocument | undefined {
  const d = profileUi(doc).dashboard;
  if (!isObject(d) || d.kind !== "device_page" || !Array.isArray(d.widgets)) return undefined;
  return d as unknown as CreavisionDashboardDocument;
}

/** `ui.widgets`, the type's suggested widgets in the profile's shape (DDB-10); undefined when the profile has none. */
export function suggestedProfileWidgets(doc: PageDocument | undefined): Record<string, unknown>[] | undefined {
  const w = profileUi(doc).widgets;
  return Array.isArray(w) ? w.filter(isObject) : undefined;
}

export const SELF = "$self";

/** A title written as an i18n key shows its text from the page's `texts`, else not at all (WEB-DDB-PAGE-03). */
const shownTitle = (t: unknown, texts: Record<string, string>) =>
  typeof t !== "string" || t === "" ? undefined : t.startsWith("i18n:") ? texts[t] || undefined : t;

/**
 * A device-page widget for one unit: `$self` in its bindings and source becomes the unit's id (DDB-20, DSH-V-04);
 * nothing else changes.
 */
export function bindSelf(
  w: CreavisionDashboardDocument["widgets"][number],
  device: string,
  texts: Record<string, string> = {},
): Widget {
  const { title, bindings, source, config, ...rest } = w;
  const shown = shownTitle(title, texts);
  return {
    ...rest,
    ...(shown ? { title: shown } : {}),
    ...(bindings ? { bindings: bindings.map((b) => (b.device === SELF ? { ...b, device } : b)) } : {}),
    ...(source
      ? {
          source: {
            ...source,
            ...(source.devices
              ? { devices: source.devices.map((d) => (d === SELF ? device : d)) as [string, ...string[]] }
              : {}),
          },
        }
      : {}),
    ...(isObject(config) ? { config } : {}),
  };
}

/** The page's widgets bound to the unit, in reading order: y, then x (DSH-02, the order phones show them in). */
export function pageWidgets(
  page: CreavisionDashboardDocument,
  device: string,
  texts: Record<string, string> = {},
): Widget[] {
  return [...page.widgets]
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)
    .map((w) => bindSelf(w, device, texts));
}
